import { formatDateStringToFE } from 'utils/date'
import { formatCentsToCurrency } from 'utils/price'
import { REV_TYPES } from 'settings/constants/revision'

export const CHANGE_SOURCE = Object.freeze({
  DIRECT: 'DIRECT',
  RENTAL_FEE_UPDATE: 'RENTAL_FEE_UPDATE',
})

export const getRows = data => (Array.isArray(data) ? data.map(({ resets = [] }) => resets).flat() : [])

/**
 * Builds action description for an INSERT version with start/end date details.
 * @param {Object} v - The INSERT version object
 * @returns {string} e.g. "added start date to 02/01/2026 and end date to 02/21/2026"
 */
const buildInsertActionDescription = v => {
  const startDate = v.startDate ?? v.start_date
  const endDate = v.endDate ?? v.end_date
  const parts = []
  if (startDate) parts.push(`start date to ${formatDateStringToFE(startDate)}`)
  if (endDate) parts.push(`end date to ${formatDateStringToFE(endDate)}`)
  if (parts.length === 0) return 'added'
  return `added ${parts.join(' and ')}`
}

/**
 * Builds action description for a DELETE version with start/end date details.
 * @param {Object} v - The DELETE version object
 * @returns {string} e.g. "deleted period 02/01/2026 – 02/21/2026"
 */
const buildDeleteActionDescription = v => {
  const startDate = v.startDate ?? v.start_date
  const endDate = v.endDate ?? v.end_date
  if (!startDate && !endDate) return 'deleted'
  const range = [startDate && formatDateStringToFE(startDate), endDate && formatDateStringToFE(endDate)].filter(Boolean).join(' – ')
  return `deleted period ${range}`
}

/**
 * Builds action description for an UPDATE version by diffing with the previous version.
 * @param {Object} prev - Previous version (same period, lower rev)
 * @param {Object} curr - Current version
 * @returns {string} e.g. "updated start date from 02/16/2026 to 02/18/2026"
 */
const buildUpdateActionDescription = (prev, curr) => {
  if (!prev) return 'updated'
  const prevStart = prev.startDate ?? prev.start_date
  const prevEnd = prev.endDate ?? prev.end_date
  const currStart = curr.startDate ?? curr.start_date
  const currEnd = curr.endDate ?? curr.end_date
  const startChanged = prevStart !== currStart
  const endChanged = prevEnd !== currEnd
  if (!startChanged && !endChanged) return 'updated'
  const parts = []
  if (startChanged) parts.push(`start date from ${formatDateStringToFE(prevStart)} to ${formatDateStringToFE(currStart)}`)
  if (endChanged) parts.push(`end date from ${formatDateStringToFE(prevEnd)} to ${formatDateStringToFE(currEnd)}`)
  return `updated ${parts.join(' and ')}`
}

/**
 * Normalizes rental period version API response to activity rows for the combined history list.
 * API returns versions with revType (INSERT/UPDATE), createdByUser/updatedByUser, createdAt, updatedAt, startDate, endDate.
 * Versions are ordered by rev ascending (oldest first).
 * @param {Array} versions - Array of version objects from GET /api/v1/versions/rental-period/{id}
 * @returns {Array} Rows with { type: 'period', createdAt, user, startDate?, endDate?, actionDescription }
 */
export const normalizePeriodVersionsToRows = (versions = []) => {
  if (!Array.isArray(versions)) return []
  return versions.map((v, i) => {
    const createdAt = v.updatedAt ?? v.updated_at ?? v.createdAt ?? v.created_at
    const { firstName = '', lastName = '' } = v.updatedByUser ?? {}
    const user = `${firstName} ${lastName}`.trim() || v.lastModifiedBy || 'Unknown'
    const revType = v.revType ?? v.rev_type
    const prev = versions[i - 1]
    let actionDescription
    if (REV_TYPES?.INSERT && revType === REV_TYPES.INSERT) actionDescription = buildInsertActionDescription(v)
    else if (REV_TYPES?.DELETE && revType === REV_TYPES.DELETE) actionDescription = buildDeleteActionDescription(v)
    else actionDescription = buildUpdateActionDescription(prev, v)
    return {
      type: 'period',
      createdAt,
      user,
      startDate: v.startDate ?? v.start_date,
      endDate: v.endDate ?? v.end_date,
      actionDescription,
    }
  })
}

/**
 * Normalizes the bulk rental period versions response (keyed by period ID) into a flat list of activity rows.
 * @param {Object} data - Response from GET /api/v1/versions/rental-fee-to-configured-service/{id}/rental-periods
 * @param {Object.<string, Array>} data.rentalPeriods - Map of period ID to version arrays
 * @returns {Array} Flat array of normalized period activity rows
 */
export const normalizeAllPeriodVersionsToRows = data => {
  if (!data?.rentalPeriods) return []
  return Object.values(data.rentalPeriods).flatMap(normalizePeriodVersionsToRows)
}

/**
 * Normalizes rental-fee-to-configured-service version records into price change activity rows.
 * Compares consecutive versions to detect amountCents changes and includes changeSource context.
 * @param {Object} data - Response from GET /api/v1/versions/rental-fee-to-configured-service/{id}
 * @param {Array} data.versions - Array of version objects
 * @returns {Array} Rows with { type, createdAt, user, actionDescription, changeSource }
 */
export const normalizePriceChangeVersionsToRows = (data = {}) => {
  const versions = data?.versions
  if (!Array.isArray(versions) || versions.length === 0) return []

  const sorted = [...versions].sort((a, b) => (a.rev ?? 0) - (b.rev ?? 0))
  const rows = []

  sorted.forEach((v, i) => {
    const revType = v.revType ?? v.rev_type
    const prevAmount = i > 0 ? sorted[i - 1].amountCents : undefined
    const currAmount = v.amountCents
    const isInsert = revType === REV_TYPES.INSERT
    const amountChanged = prevAmount !== undefined && prevAmount !== currAmount

    if (!isInsert && !amountChanged) return

    const createdAt = v.updatedAt ?? v.updated_at ?? v.createdAt ?? v.created_at
    const { firstName = '', lastName = '' } = v.updatedByUser ?? v.createdByUser ?? {}
    const user = `${firstName} ${lastName}`.trim() || v.lastModifiedBy || 'Unknown'
    const changeSource = v.changeSource

    let actionDescription
    if (isInsert) {
      actionDescription = `set price to ${formatCentsToCurrency(currAmount)}`
    } else {
      actionDescription = `changed price from ${formatCentsToCurrency(prevAmount)} to ${formatCentsToCurrency(currAmount)}`
    }

    rows.push({ type: 'price_change', createdAt, user, actionDescription, changeSource })
  })

  return rows
}
